/**
 * LEOS Mission Ready Main Interface
 * Updated to reflect current SDA-heavy, learning-focused system
 */

import { unifiedAuth } from '../auth/unified-auth.js';
import { UserProfileOverlay } from './user-profile-overlay.js';
import { apiClient } from './api-integration.js';
import { groundStationUploadUI } from './ground-station-upload-refactored.js';
import { NavigationModule } from './main-interface-navigation.js';
import { UIModule } from './main-interface-ui.js';

export class MissionReadyInterface {
    constructor() {
        this.currentSection = 'mission-control';
        this.user = null; // Will be set during init()
        console.log('MissionReadyInterface - Current user:', this.user);
        this.userProfileOverlay = null;
        this.apiClient = apiClient;
        this.activityLog = [];
        this.maxActivityItems = 20;
        this.welcomeMessageIndex = 0;
        this.welcomeMessageTimer = null;
        
        // Updated navigation structure - consolidated sections
        this.sections = {
            'mission-control': {
                icon: '3d.svg',
                label: 'Mission Control',
                description: 'Unified operations center',
                available: true
            },
            'space-domain': {
                icon: 'sat.svg',
                label: 'Space Domain Awareness',
                description: '58,000+ tracked objects',
                available: true
            },
            'learning': {
                icon: 'learn.svg',
                label: 'Training Center',
                description: 'Interactive tutorials',
                available: true
            },
            'data-sources': {
                icon: 'support.svg',
                label: 'Data Integration',
                description: 'Configure data feeds',
                available: true
            },
            'mission-support': {
                icon: 'support.svg',
                label: 'Mission Support',
                description: 'Help & ticketing',
                available: true
            },
            'settings': {
                icon: 'email.svg',
                label: 'Settings',
                description: 'System configuration',
                available: true
            }
        };
    }

    init() {
        // Update user data from auth service
        this.user = unifiedAuth.currentUser;
        console.log('MissionReadyInterface init - User:', this.user);
        
        // If user is still null, show login screen
        if (!this.user) {
            console.error('No user found during Mission Ready init');
            if (window.unifiedAuth && window.unifiedAuth.showLoginScreen) {
                window.unifiedAuth.showLoginScreen();
            }
            return;
        }
        
        this.createMainInterface();
        this.attachEventListeners();
        
        // Initialize UI module
        this.uiModule = new UIModule(this);
        
        // Initialize navigation module
        this.navigationModule = new NavigationModule(this);
        
        this.showSection('mission-control');
        
        // Initialize user profile overlay
        this.userProfileOverlay = new UserProfileOverlay(this.user);
        
        // Start periodic stats update
        this.startStatsUpdate();
        this.startWelcomeMessageRotation();
    }
    
    startStatsUpdate() {
        // Update stats every 2 seconds
        this.statsInterval = setInterval(() => {
            this.updateMissionStats();
        }, 2000);
    }
    
    updateMissionStats() {
        // This method updates mission statistics
        // Currently handled by individual tab components
        // Can be expanded to update global stats if needed
    }
    
    showStatInfo(statType) {
        const info = {
            satellites: {
                title: 'Active Satellites',
                content: 'Currently tracking BULLDOG and CyberRTS satellites in low Earth orbit. These satellites provide real-time telemetry data and communication capabilities.'
            },
            ground: {
                title: 'Ground Station Network',
                content: 'Our network includes 5 strategic ground stations: Canberra DSN Site, Vandenberg CA, Wallops Island VA, Mauna Kea Station, and Diego Garcia, providing global coverage.'
            },
            data: {
                title: 'Data Rate',
                content: 'Real-time telemetry data throughput from all active satellites. Rate increases when satellites are in line-of-sight with ground stations.'
            },
            missions: {
                title: 'Active Missions',
                content: 'Operational missions currently being executed. Includes satellite deployments, data collection campaigns, and orbital maneuvers.'
            }
        };
        
        const statInfo = info[statType];
        if (statInfo) {
            // Create a simple tooltip
            const tooltip = document.createElement('div');
            tooltip.style.cssText = `
                position: fixed;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                background: rgba(15, 23, 42, 0.98);
                border: 1px solid rgba(0, 212, 255, 0.3);
                border-radius: 12px;
                padding: 24px;
                max-width: 400px;
                z-index: 10000;
                box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
            `;
            
            tooltip.innerHTML = `
                <h3 style="color: #00d4ff; margin: 0 0 12px 0; font-size: 18px;">${statInfo.title}</h3>
                <p style="color: #e2e8f0; margin: 0 0 16px 0; line-height: 1.5;">${statInfo.content}</p>
                <button onclick="this.parentElement.remove()" style="
                    background: rgba(0, 153, 255, 0.2);
                    border: 1px solid rgba(0, 153, 255, 0.3);
                    border-radius: 6px;
                    padding: 8px 16px;
                    color: #00d4ff;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                ">Close</button>
            `;
            
            document.body.appendChild(tooltip);
            
            // Auto-remove after 5 seconds
            setTimeout(() => {
                if (tooltip.parentElement) {
                    tooltip.remove();
                }
            }, 5000);
        }
    }

    createMainInterface() {
        const mainContainer = document.createElement('div');
        mainContainer.id = 'mission-ready-interface';
        mainContainer.className = 'mission-ready-interface';
        
        mainContainer.innerHTML = `
            <div class="sidebar-navigation">
                <div class="sidebar-header">
                    <a href="https://cyberrts.com" target="_blank" rel="noopener noreferrer" class="logo-section" style="text-decoration: none; color: inherit; cursor: pointer;" title="Visit CyberRTS">
                        <img src="assets/CyberRTS-Simple-Logo.svg" alt="CyberRTS" class="nav-logo" />
                        <div class="logo-text" style="margin-left: 35px;">
                            <div class="app-tier" style="font-size: 14px; letter-spacing: 1.5px; font-weight: 700; line-height: 1; margin-bottom: 2px;">MISSION</div>
                            <div class="app-tier" style="font-size: 14px; letter-spacing: 1.5px; font-weight: 700; line-height: 1;">READY</div>
                        </div>
                    </a>
                </div>
                
                <div class="user-profile" id="user-profile-section" style="padding: 16px 20px; cursor: pointer; border-bottom: 1px solid rgba(148, 163, 184, 0.1); transition: background 0.2s ease;">
                    <img src="${this.user?.avatar || 'assets/kelly.svg'}" alt="User" class="user-avatar" style="width: 36px; height: 36px;" />
                    <div class="user-info">
                        <div class="user-name" style="font-size: 14px;">${this.user?.name || 'User'}</div>
                        <div class="user-role" style="font-size: 11px; opacity: 0.7;">${this.user?.role || 'Role'}</div>
                    </div>
                </div>
                
                <nav class="main-navigation">
                    ${Object.entries(this.sections).map(([key, section]) => `
                        <div class="nav-item ${!section.available ? 'locked' : ''}" data-section="${key}" title="${section.label}" style="padding: 12px 20px;">
                            <div class="nav-icon" style="width: 20px; height: 20px;">
                                <img src="assets/${section.icon}" alt="${section.label}" />
                            </div>
                            <div class="nav-content">
                                <div class="nav-label" style="font-size: 14px;">${section.label}</div>
                            </div>
                            ${!section.available ? '<div class="nav-lock"><img src="assets/email.svg" alt="Locked" style="width: 14px; height: 14px; filter: brightness(0) saturate(100%) invert(40%);" /></div>' : ''}
                        </div>
                    `).join('')}
                </nav>
                
            </div>
            
            <div class="main-content">
                <div class="top-bar" style="height: 60px; padding: 0 24px;">
                    <div class="current-section">
                        <h1 id="section-title" style="font-size: 20px;">Space Domain Awareness</h1>
                        <p id="section-subtitle" style="font-size: 13px;">58,247 tracked objects</p>
                    </div>
                    <div class="top-bar-controls">
                        <div class="connection-status">
                            <span class="status-dot online"></span>
                            <span style="font-size: 13px;">Connected</span>
                        </div>
                    </div>
                </div>
                
                <div class="content-area" id="content-area">
                    <!-- Dynamic content will be loaded here -->
                </div>
            </div>
        `;
        
        document.body.appendChild(mainContainer);
    }

    attachEventListeners() {
        // Navigation item clicks
        document.querySelectorAll('.nav-item').forEach(item => {
            item.addEventListener('click', () => {
                const section = item.dataset.section;
                console.log('Nav item clicked:', section);
                const sectionConfig = this.sections[section];
                
                if (!sectionConfig.available) {
                    this.showUpgradeModal(sectionConfig.label);
                    return;
                }
                
                this.showSection(section);
            });
        });
        
        // Click handler for user profile
        const userProfile = document.getElementById('user-profile-section');
        if (userProfile) {
            userProfile.addEventListener('click', (e) => {
                e.stopPropagation();
                this.showUserProfile();
            });
            
            // Add hover effect
            userProfile.addEventListener('mouseenter', () => {
                userProfile.style.background = 'rgba(148, 163, 184, 0.05)';
            });
            userProfile.addEventListener('mouseleave', () => {
                userProfile.style.background = 'transparent';
            });
        }
        
        // Click handler for sidebar toggle arrow
        const sidebar = document.querySelector('.sidebar-navigation');
        if (sidebar) {
            sidebar.addEventListener('click', (e) => {
                // Check if click is on the ::after pseudo-element (the arrow)
                const sidebarRect = sidebar.getBoundingClientRect();
                const clickX = e.clientX - sidebarRect.left;
                const sidebarWidth = sidebar.classList.contains('collapsed') ? 80 : 260;
                
                // If click is beyond the sidebar width (on the arrow)
                if (clickX > sidebarWidth - 20) {
                    e.stopPropagation();
                    this.toggleSidebar();
                }
            });
        }
    }

    showSection(sectionKey) {
        console.log('showSection called with:', sectionKey);
        
        // If switching away from mission control, preserve the canvas
        if (this.currentSection === 'mission-control' && sectionKey !== 'mission-control') {
            const canvas = document.getElementById('renderCanvas');
            const originalContainer = document.getElementById('renderCanvas-container');
            
            if (canvas && originalContainer && !originalContainer.contains(canvas)) {
                // Move canvas back to original container
                originalContainer.appendChild(canvas);
                canvas.style.width = '100%';
                canvas.style.height = '100%';
            }
        }
        
        // Update active navigation
        document.querySelectorAll('.nav-item').forEach(item => {
            item.classList.toggle('active', item.dataset.section === sectionKey);
        });
        
        this.currentSection = sectionKey;
        const section = this.sections[sectionKey];
        
        // Update top bar
        document.getElementById('section-title').textContent = section.label;
        document.getElementById('section-subtitle').textContent = this.getSectionSubtitle(sectionKey);
        
        // Load section content using navigation module
        if (this.navigationModule) {
            this.navigationModule.showSection(sectionKey);
        } else {
            console.error('Navigation module not initialized');
        }
    }

    getSectionSubtitle(sectionKey) {
        const subtitles = {
            'mission-control': `Welcome back, ${this.user?.name || 'Commander'}`,
            'space-domain': 'Monitoring 58,247 tracked objects in real-time',
            'learning': 'Interactive space operations training',
            'data-sources': 'Configure data feeds and integrations',
            'mission-support': 'Help center, documentation & support tickets',
            'settings': 'System preferences and account management'
        };
        
        return subtitles[sectionKey] || '';
    }
    
    startWelcomeMessageRotation() {
        const messages = [
            `Welcome back, ${this.user?.name || 'Commander'}`,
            'Real-time satellite tracking active',
            'All systems operational',
            'Mission control standing by',
            'Space domain monitoring enabled',
            'Ground network online',
            '2 satellites in active tracking',
            'Telemetry streams nominal'
        ];
        
        // Set initial welcome message for 15 seconds
        setTimeout(() => {
            this.welcomeMessageTimer = setInterval(() => {
                // Only rotate messages if we're on mission control tab
                if (this.currentSection === 'mission-control') {
                    this.welcomeMessageIndex = (this.welcomeMessageIndex + 1) % messages.length;
                    const subtitleElement = document.getElementById('section-subtitle');
                    if (subtitleElement) {
                        subtitleElement.textContent = messages[this.welcomeMessageIndex];
                    }
                }
            }, 5000); // Rotate every 5 seconds after initial 15 seconds
        }, 15000); // Wait 15 seconds before starting rotation
    }

    async showMissionControl() {
        const contentArea = document.getElementById('content-area');
        
        // Import and initialize the enhanced mission control
        const { createMissionControlEnhanced } = await import('./mission-control-enhanced.js');
        this.missionControl = createMissionControlEnhanced(contentArea);
        
        // Make it available globally for navigation
        window.missionControlEnhanced = this.missionControl;
        
        return; // Using the new enhanced implementation
    }

    async showSpaceDomainAwareness() {
        // Lazy load the SDA interface module
        if (!this.sdaInterface) {
            const { SDAInterface } = await import('./sda-interface.js');
            this.sdaInterface = new SDAInterface(this);
        }
        
        const contentArea = document.getElementById('content-area');
        contentArea.innerHTML = this.sdaInterface.createInterface();
        
        // Move canvas to SDA view and ensure Earth is visible
        this.sdaInterface.moveCanvasToContainer();
        this.uiModule.hideOldUI();
        this.sdaInterface.setupHandlers();
        await this.sdaInterface.initializeVisualization();
    }
    
    moveCanvasToSDAContainer() {
        // Deprecated - now handled by SDAInterface
        if (this.sdaInterface) {
            this.sdaInterface.moveCanvasToContainer();
        }
    }
    
    setupSDAHandlers() {
        // Deprecated - now handled by SDAInterface
        if (this.sdaInterface) {
            this.sdaInterface.setupHandlers();
        }
    }
    
    initializeSDAVisualization() {
        // Deprecated - now handled by SDAInterface
        if (this.sdaInterface) {
            this.sdaInterface.initializeVisualization();
        }
    }
    
    setupSimulationTimeControls() {
        const simTimeDisplay = document.getElementById('simulation-time-display');
        const simControlsPanel = document.getElementById('sim-time-controls');
        const speedBtns = Array.from(document.querySelectorAll('#sim-time-controls .time-btn.speed-btn'));
        
        let controlsVisible = false;
        
        if (!simTimeDisplay || !simControlsPanel) return;
        
        // Update button states
        function updateSpeedBtns() {
            if (window.simState) {
                speedBtns.forEach(btn => {
                    const mult = parseFloat(btn.getAttribute('data-mult'));
                    if (Math.abs(window.simState.timeMultiplier - mult) < 0.001) {
                        btn.classList.add('active');
                    } else {
                        btn.classList.remove('active');
                    }
                });
            }
        }
        
        
        // Show/hide controls panel on time display click
        simTimeDisplay.addEventListener('click', (e) => {
            if (e.target.classList.contains('time-btn')) return;
            controlsVisible = !controlsVisible;
            simControlsPanel.classList.toggle('hidden', !controlsVisible);
            updateSpeedBtns();
        });
        
        // Speed button clicks
        speedBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const mult = parseFloat(btn.getAttribute('data-mult'));
                if (window.simState && window.simState.timeMultiplier !== mult) {
                    window.simState.timeMultiplier = mult;
                    window.simState.lastTimeMultiplier = mult;
                }
                updateSpeedBtns();
            });
        });
        
        
        // Hide controls if clicking outside
        document.addEventListener('mousedown', (e) => {
            if (controlsVisible && 
                !simTimeDisplay.contains(e.target) && 
                !simControlsPanel.contains(e.target)) {
                simControlsPanel.classList.add('hidden');
                controlsVisible = false;
            }
        });
        
    }

    moveCanvasToContainer() {
        const canvas = document.getElementById('renderCanvas');
        const canvasContainer = document.getElementById('canvas-container');
        
        if (canvas && canvasContainer) {
            // Make sure canvas is visible before moving it
            canvas.style.display = 'block';
            canvas.style.visibility = 'visible';
            canvas.style.opacity = '1';
            
            canvasContainer.appendChild(canvas);
            canvas.style.width = '100%';
            canvas.style.height = '100%';
            
            // Force a resize to ensure proper rendering
            if (window.app && window.app.engine) {
                window.app.engine.resize();
                
                // Ensure render loop is running
                if (!window.app.engine.isDisposed) {
                    window.app.engine.runRenderLoop(() => {
                        if (window.app.scene) {
                            window.app.scene.render();
                        }
                    });
                }
            }
            
            // Trigger resize event
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 100);
        }
    }

    initializeDraggableTiles() {
        const tiles = document.querySelectorAll('.mission-dashboard-tile');
        
        tiles.forEach(tile => {
            let isDragging = false;
            let currentX;
            let currentY;
            let initialX;
            let initialY;
            let xOffset = 0;
            let yOffset = 0;

            tile.addEventListener('mousedown', dragStart);
            tile.addEventListener('mouseup', dragEnd);
            tile.addEventListener('mousemove', drag);

            function dragStart(e) {
                if (e.target.tagName === 'BUTTON' || e.target.tagName === 'INPUT') {
                    return;
                }
                
                initialX = e.clientX - xOffset;
                initialY = e.clientY - yOffset;

                if (e.target === tile) {
                    isDragging = true;
                    tile.style.position = 'relative';
                    tile.style.zIndex = '1000';
                }
            }

            function dragEnd(e) {
                initialX = currentX;
                initialY = currentY;

                isDragging = false;
                tile.style.zIndex = '10';
            }

            function drag(e) {
                if (e.preventDefault) {
                    e.preventDefault();
                }

                if (isDragging) {
                    currentX = e.clientX - initialX;
                    currentY = e.clientY - initialY;

                    xOffset = currentX;
                    yOffset = currentY;

                    tile.style.transform = `translate(${currentX}px, ${currentY}px)`;
                }
            }
        });
    }

    toggleFullscreen() {
        const visualizationContainer = document.querySelector('.visualization-container');
        const canvas = document.getElementById('renderCanvas');
        const fullscreenBtn = document.getElementById('fullscreen-btn');
        
        if (!document.fullscreenElement) {
            // Enter fullscreen
            if (visualizationContainer.requestFullscreen) {
                visualizationContainer.requestFullscreen();
            } else if (visualizationContainer.webkitRequestFullscreen) {
                visualizationContainer.webkitRequestFullscreen();
            } else if (visualizationContainer.msRequestFullscreen) {
                visualizationContainer.msRequestFullscreen();
            }
            
            // Update button icon
            fullscreenBtn.innerHTML = '<img src="assets/compress.svg" alt="Exit Fullscreen" style="width: 20px; height: 20px; filter: brightness(0) saturate(100%) invert(73%) sepia(77%) saturate(1462%) hue-rotate(166deg) brightness(104%) contrast(103%);" />';
            fullscreenBtn.title = 'Exit Fullscreen';
            
            // Ensure canvas fills the fullscreen
            visualizationContainer.style.padding = '0';
            if (canvas) {
                canvas.style.width = '100vw';
                canvas.style.height = '100vh';
            }
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
            
            // Update button icon
            fullscreenBtn.innerHTML = '<img src="assets/expand.svg" alt="Fullscreen" style="width: 20px; height: 20px; filter: brightness(0) saturate(100%) invert(73%) sepia(77%) saturate(1462%) hue-rotate(166deg) brightness(104%) contrast(103%);" />';
            fullscreenBtn.title = 'Toggle Fullscreen';
            
            // Reset canvas size
            visualizationContainer.style.padding = '';
            if (canvas) {
                canvas.style.width = '100%';
                canvas.style.height = '100%';
            }
        }
        
        // Resize engine after transition
        setTimeout(() => {
            if (window.app && window.app.engine) {
                window.app.engine.resize();
            }
        }, 100);
    }

    hideOldUI() {
        // Don't show CAPCOM button yet - wait for simulation to load
        const capcomBtn = document.getElementById('capcom-kelly-btn');
        if (capcomBtn) {
            capcomBtn.style.display = 'none';
        }
        
        // Make sure mission dashboard is available but hidden
        const missionDashboard = document.getElementById('mission-dashboard');
        if (missionDashboard) {
            missionDashboard.classList.add('hidden');
            missionDashboard.style.display = '';  // Remove any inline display:none
        }
        
        // Hide old floating UI elements
        const oldUIElements = [
            'sda-toggle-btn', 
            'simulation-settings-btn',
            'add-tle-button',
            'telemetry-dashboard-btn',
            'data-source-btn',
            'leos-dial',
            'kelcodes',
            'time-display-container',
            'time-display',
            'welcome-modal',
            'loading-screen',
            'control-dock-container',
            'control-dock',
            'minimized-dock',
            'learning-btn',
            'support-btn',
            'dock-minimize-btn',
            'cyberrts-logo-link'
        ];
        
        oldUIElements.forEach(id => {
            const element = document.getElementById(id);
            if (element) element.style.display = 'none';
        });
    }

    async showLearningCenter() {
        const contentArea = document.getElementById('content-area');
        
        // Import and initialize the learning center
        const { learningCenter } = await import('./learning-center.js');
        
        // Create learning UI
        const learningUI = learningCenter.createLearningUI();
        contentArea.innerHTML = '';
        contentArea.appendChild(learningUI);
        
        // Initialize learning center
        learningCenter.init();
        
        // Make learning center globally available
        window.learningCenter = learningCenter;
    }

    // Integration methods for existing functionality
    toggleSDA() {
        const sdaToggleBtn = document.getElementById('sda-toggle-btn');
        if (sdaToggleBtn) {
            sdaToggleBtn.click();
        }
    }

    addTLE() {
        const addTLEBtn = document.getElementById('add-tle-button');
        if (addTLEBtn) {
            addTLEBtn.click();
        }
    }

    openDataManager() {
        // Trigger Ctrl+D for data source manager
        const event = new KeyboardEvent('keydown', {
            key: 'd',
            ctrlKey: true,
            bubbles: true
        });
        document.dispatchEvent(event);
    }

    toggleSidebar() {
        const sidebar = document.querySelector('.sidebar-navigation');
        const toggleIcon = document.querySelector('.toggle-icon');
        const isCollapsed = sidebar.classList.contains('collapsed');
        
        if (isCollapsed) {
            // Expand sidebar
            sidebar.classList.remove('collapsed');
            if (toggleIcon) {
                toggleIcon.innerHTML = '<img src="assets/chevron-left.svg" alt="Collapse" style="width: 16px; height: 16px; filter: brightness(0) saturate(100%) invert(64%) sepia(8%) saturate(507%) hue-rotate(184deg) brightness(91%) contrast(87%);" />';
            }
        } else {
            // Collapse sidebar
            sidebar.classList.add('collapsed');
            if (toggleIcon) {
                toggleIcon.innerHTML = '<img src="assets/chevron-right.svg" alt="Expand" style="width: 16px; height: 16px; filter: brightness(0) saturate(100%) invert(64%) sepia(8%) saturate(507%) hue-rotate(184deg) brightness(91%) contrast(87%);" />';
            }
        }
        
        // Trigger canvas resize after transition
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
            // Also trigger a manual resize of the canvas if needed
            const canvas = document.getElementById('renderCanvas');
            if (canvas && window.engine) {
                window.engine.resize();
            }
        }, 350);
    }
    
    // Data Integration methods
    uploadSatelliteData() {
        // Lazy load the data upload UI module
        import('./data-upload-ui.js').then(module => {
            module.dataUploadUI.show();
        }).catch(err => {
            console.error('Failed to load data upload UI:', err);
            this.showNotification('Failed to load upload interface', 'error');
        });
    }
    
    showLiveDataFeeds() {
        // Lazy load the data feeds UI module
        import('./data-feeds-ui.js').then(module => {
            module.dataFeedsUI.show();
        }).catch(err => {
            console.error('Failed to load data feeds UI:', err);
            this.showNotification('Failed to load data feeds interface', 'error');
        });
    }
    
    uploadGroundStations() {
        if (groundStationUploadUI) {
            groundStationUploadUI.show();
        }
    }
    
    // Mission Support methods
    loadSupportTickets() {
        // In a real implementation, this would fetch from an API
        const ticketsList = document.getElementById('support-tickets-list');
        const tickets = JSON.parse(localStorage.getItem('support-tickets') || '[]');
        
        if (tickets.length === 0) {
            return; // Keep the "no tickets" message
        }
        
        ticketsList.innerHTML = tickets.map(ticket => `
            <div style="background: rgba(30, 41, 59, 0.4); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 8px; padding: 16px; margin-bottom: 12px; cursor: pointer; transition: all 0.2s;"
                 onclick="missionInterface.viewTicket('${ticket.id}')"
                 onmouseover="this.style.background='rgba(30, 41, 59, 0.6)'; this.style.borderColor='rgba(0, 212, 255, 0.3)';"
                 onmouseout="this.style.background='rgba(30, 41, 59, 0.4)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                <div style="display: flex; justify-content: space-between; align-items: start;">
                    <div>
                        <h4 style="color: #e2e8f0; font-size: 16px; font-weight: 500; margin-bottom: 4px;">${ticket.subject}</h4>
                        <p style="color: #94a3b8; font-size: 14px; margin-bottom: 8px;">${ticket.description.substring(0, 100)}...</p>
                        <div style="display: flex; gap: 16px; font-size: 12px; color: #64748b;">
                            <span>Ticket #${ticket.id}</span>
                            <span>${new Date(ticket.created).toLocaleDateString()}</span>
                        </div>
                    </div>
                    <div style="text-align: right;">
                        <span style="padding: 4px 12px; border-radius: 4px; font-size: 12px; font-weight: 500; background: ${ticket.status === 'open' ? 'rgba(251, 191, 36, 0.1)' : ticket.status === 'resolved' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(148, 163, 184, 0.1)'}; color: ${ticket.status === 'open' ? '#fbbf24' : ticket.status === 'resolved' ? '#10b981' : '#94a3b8'};">
                            ${ticket.status.toUpperCase()}
                        </span>
                    </div>
                </div>
            </div>
        `).join('');
    }
    
    showNewTicketForm() {
        const modal = document.createElement('div');
        modal.className = 'support-ticket-modal';
        modal.innerHTML = `
            <div class="modal-backdrop" onclick="document.querySelector('.support-ticket-modal').remove()"></div>
            <div class="modal-container">
                <div class="modal-header">
                    <h2>Create Support Ticket</h2>
                    <button class="close-btn" onclick="document.querySelector('.support-ticket-modal').remove()">×</button>
                </div>
                <form id="support-ticket-form" onsubmit="missionInterface.submitTicket(event); return false;">
                    <div class="form-group">
                        <label>Subject</label>
                        <input type="text" id="ticket-subject" required placeholder="Brief description of the issue" />
                    </div>
                    
                    <div class="form-group">
                        <label>Category</label>
                        <select id="ticket-category" required>
                            <option value="">Select a category</option>
                            <option value="technical">Technical Issue</option>
                            <option value="account">Account/Billing</option>
                            <option value="feature">Feature Request</option>
                            <option value="data">Data/API</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    
                    <div class="form-group">
                        <label>Description</label>
                        <textarea id="ticket-description" required rows="6" placeholder="Please describe your issue in detail..."></textarea>
                    </div>
                    
                    <div class="form-group">
                        <label>Attachments (optional)</label>
                        <input type="file" id="ticket-attachments" multiple accept="image/*,.pdf,.txt,.log" />
                        <p style="font-size: 12px; color: #64748b; margin-top: 4px;">Accepted: Images, PDF, TXT, LOG files (max 10MB each)</p>
                    </div>
                    
                    <div class="form-actions">
                        <button type="button" class="btn-secondary" onclick="document.querySelector('.support-ticket-modal').remove()">Cancel</button>
                        <button type="submit" class="btn-primary">Submit Ticket</button>
                    </div>
                </form>
            </div>
        `;
        
        // Add styles
        this.addTicketModalStyles();
        
        document.body.appendChild(modal);
        
        // Focus first input
        setTimeout(() => {
            document.getElementById('ticket-subject')?.focus();
        }, 100);
    }
    
    addTicketModalStyles() {
        if (document.getElementById('ticket-modal-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'ticket-modal-styles';
        style.textContent = `
            .support-ticket-modal {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 10000;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            
            .modal-backdrop {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, 0.8);
                backdrop-filter: blur(5px);
            }
            
            .modal-container {
                position: relative;
                background: rgba(15, 23, 42, 0.95);
                border: 1px solid rgba(0, 212, 255, 0.3);
                border-radius: 16px;
                width: 90%;
                max-width: 600px;
                max-height: 90vh;
                overflow-y: auto;
                box-shadow: 0 0 40px rgba(0, 212, 255, 0.2);
            }
            
            .modal-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 24px;
                border-bottom: 1px solid rgba(148, 163, 184, 0.1);
            }
            
            .modal-header h2 {
                margin: 0;
                color: #e2e8f0;
                font-size: 24px;
                font-weight: 600;
            }
            
            .close-btn {
                background: none;
                border: none;
                color: #64748b;
                font-size: 28px;
                cursor: pointer;
                padding: 0;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                transition: all 0.2s;
            }
            
            .close-btn:hover {
                background: rgba(148, 163, 184, 0.1);
                color: #e2e8f0;
            }
            
            #support-ticket-form {
                padding: 24px;
            }
            
            .form-group {
                margin-bottom: 20px;
            }
            
            .form-group label {
                display: block;
                color: #94a3b8;
                font-size: 14px;
                font-weight: 500;
                margin-bottom: 8px;
            }
            
            .form-group input,
            .form-group select,
            .form-group textarea {
                width: 100%;
                padding: 12px 16px;
                background: rgba(30, 41, 59, 0.5);
                border: 1px solid rgba(148, 163, 184, 0.2);
                border-radius: 8px;
                color: #e2e8f0;
                font-size: 14px;
                transition: all 0.2s;
                box-sizing: border-box;
            }
            
            .form-group input:focus,
            .form-group select:focus,
            .form-group textarea:focus {
                outline: none;
                border-color: #00d4ff;
                background: rgba(30, 41, 59, 0.8);
            }
            
            .form-group textarea {
                resize: vertical;
                min-height: 120px;
            }
            
            .form-actions {
                display: flex;
                gap: 12px;
                justify-content: flex-end;
                margin-top: 24px;
            }
            
            .btn-primary,
            .btn-secondary {
                padding: 12px 24px;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s;
                border: none;
            }
            
            .btn-primary {
                background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
                color: white;
            }
            
            .btn-primary:hover {
                transform: translateY(-1px);
                box-shadow: 0 4px 12px rgba(0, 212, 255, 0.3);
            }
            
            .btn-secondary {
                background: rgba(148, 163, 184, 0.2);
                color: #e2e8f0;
            }
            
            .btn-secondary:hover {
                background: rgba(148, 163, 184, 0.3);
            }
        `;
        document.head.appendChild(style);
    }
    
    submitTicket(event) {
        event.preventDefault();
        
        const subject = document.getElementById('ticket-subject').value;
        const category = document.getElementById('ticket-category').value;
        const description = document.getElementById('ticket-description').value;
        const attachments = document.getElementById('ticket-attachments').files;
        
        // Create ticket object
        const ticket = {
            id: Date.now().toString(36),
            subject,
            category,
            description,
            status: 'open',
            created: new Date().toISOString(),
            attachments: Array.from(attachments).map(f => f.name)
        };
        
        // Save to localStorage (in real app, would send to API)
        const tickets = JSON.parse(localStorage.getItem('support-tickets') || '[]');
        tickets.unshift(ticket);
        localStorage.setItem('support-tickets', JSON.stringify(tickets));
        
        // Show success notification
        if (window.showMRNotification) {
            window.showMRNotification(`Support ticket #${ticket.id} created successfully`, 'success');
        }
        
        // Close modal and reload tickets
        document.querySelector('.support-ticket-modal').remove();
        this.loadSupportTickets();
    }
    
    viewTicket(ticketId) {
        // In a real app, this would open a detailed ticket view
        if (window.showMRNotification) {
            window.showMRNotification(`Opening ticket #${ticketId}...`, 'info');
        }
    }
    
    showFAQs() {
        const contentArea = document.getElementById('content-area');
        contentArea.innerHTML = `
            <div style="padding: 24px; max-width: 1000px; margin: 0 auto;">
                <div style="margin-bottom: 32px;">
                    <button onclick="missionInterface.showSection('mission-support')" style="display: inline-flex; align-items: center; gap: 8px; background: none; border: none; color: #00d4ff; font-size: 14px; cursor: pointer; padding: 8px 0; margin-bottom: 16px;">
                        <span>←</span> Back to Support Center
                    </button>
                    <h2 style="color: #e2e8f0; font-size: 28px; font-weight: 600; margin-bottom: 8px;">Frequently Asked Questions</h2>
                    <p style="color: #94a3b8; font-size: 16px;">Find answers to common questions about Mission Ready</p>
                </div>
                
                <div style="display: flex; flex-direction: column; gap: 16px;">
                    ${this.getFAQItems().map(faq => `
                        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 12px; padding: 24px;">
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
                                <span style="color: #00d4ff;">Q:</span> ${faq.question}
                            </h3>
                            <p style="color: #94a3b8; font-size: 14px; line-height: 1.6;">
                                <span style="color: #10b981; font-weight: 600;">A:</span> ${faq.answer}
                            </p>
                        </div>
                    `).join('')}
                </div>
                
                <div style="margin-top: 40px; padding: 24px; background: rgba(0, 212, 255, 0.1); border: 1px solid rgba(0, 212, 255, 0.2); border-radius: 12px; text-align: center;">
                    <p style="color: #e2e8f0; font-size: 16px; margin-bottom: 12px;">Can't find what you're looking for?</p>
                    <button onclick="missionInterface.showNewTicketForm()" style="background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%); color: white; border: none; padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 500; cursor: pointer;">
                        Create Support Ticket
                    </button>
                </div>
            </div>
        `;
    }
    
    getFAQItems() {
        return [
            {
                question: "How do I upload satellite TLE data?",
                answer: "Navigate to Data Sources in the main menu, then click on 'Upload Satellites'. You can either paste TLE data directly or upload a file containing TLE information. The system supports standard two-line element format."
            },
            {
                question: "What telemetry formats are supported?",
                answer: "Mission Ready supports various telemetry formats including JSON, CSV, and real-time streaming data through WebSocket connections. You can configure custom data mappings in the Data Source Manager."
            },
            {
                question: "How can I set up collision alerts?",
                answer: "Collision alerts are automatically configured when you access the Space Domain Awareness module. You can customize alert thresholds and notification preferences in the SDA settings panel."
            },
            {
                question: "Is there an API for external integrations?",
                answer: "Yes, Mission Ready provides a comprehensive REST API for external integrations. Full API documentation is available at docs.missionready.space/api"
            },
            {
                question: "How do I export mission data?",
                answer: "You can export mission data from the Analytics section. Multiple export formats are supported including CSV, JSON, and KML for visualization in other tools."
            }
        ];
    }
    
    showDataSources() {
        const contentArea = document.getElementById('content-area');
        contentArea.innerHTML = `
            <div class="data-sources-dashboard" style="padding: 24px;">
                <div class="data-header" style="margin-bottom: 32px;">
                    <h2 style="color: #e2e8f0; font-size: 24px; font-weight: 600; margin: 0 0 8px 0;">Data Integration Hub</h2>
                    <p style="color: #94a3b8; font-size: 14px; margin: 0;">Configure telemetry feeds, upload satellite data, and manage integrations</p>
                </div>
                
                <div class="data-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px;">
                    <!-- Upload Satellite Data -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.uploadSatelliteData()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(0, 212, 255, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(0, 153, 255, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/sat.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Upload Satellites</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Import TLE/NORAD data or manually create satellites</p>
                        <div style="color: #00d4ff; font-size: 13px; font-weight: 500;">Click to upload →</div>
                    </div>
                    
                    <!-- Live Data Feeds -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.showLiveDataFeeds()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(16, 185, 129, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(16, 185, 129, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/telem.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Live Data Feeds</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Connect to real-time telemetry streams</p>
                        <div style="color: #10b981; font-size: 13px; font-weight: 500;">Configure feeds →</div>
                    </div>
                    
                    <!-- API Integration -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="window.showMRNotification && window.showMRNotification('API Integration - Coming Soon', 'info')"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(251, 191, 36, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(251, 191, 36, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/3d.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">API Integration</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Connect external APIs for automated data ingestion</p>
                        <div style="color: #fbbf24; font-size: 13px; font-weight: 500;">Coming Soon</div>
                    </div>
                    
                    <!-- Ground Station Network -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.uploadGroundStations()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(168, 85, 247, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(168, 85, 247, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/antenna.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Ground Stations</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Configure and manage ground station network</p>
                        <div style="color: #a855f7; font-size: 13px; font-weight: 500;">Manage network →</div>
                    </div>
                    
                    <!-- Data Source Manager -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="missionInterface.openDataManager()"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(239, 68, 68, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(239, 68, 68, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/telem.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Data Source Manager</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Advanced telemetry configuration</p>
                        <div style="color: #ef4444; font-size: 13px; font-weight: 500;">Open manager →</div>
                    </div>
                    
                    <!-- Export Data -->
                    <div class="data-card" style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px; cursor: pointer; transition: all 0.2s ease;"
                         onclick="window.showMRNotification && window.showMRNotification('Export functionality coming soon', 'info')"
                         onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(59, 130, 246, 0.3)';"
                         onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                        <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 16px;">
                            <div style="width: 48px; height: 48px; background: rgba(59, 130, 246, 0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
                                <img src="assets/support.svg" style="width: 24px; height: 24px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            </div>
                            <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0;">Export Data</h3>
                        </div>
                        <p style="color: #94a3b8; font-size: 14px; margin: 0 0 16px 0;">Export telemetry and orbital data</p>
                        <div style="color: #3b82f6; font-size: 13px; font-weight: 500;">Coming Soon</div>
                    </div>
                </div>
                
                <!-- Data Stats -->
                <div style="margin-top: 40px; padding: 24px; background: rgba(15, 23, 42, 0.4); border: 1px solid rgba(148, 163, 184, 0.1); border-radius: 16px;">
                    <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin: 0 0 20px 0;">Data Overview</h3>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px;">
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Active Data Sources</div>
                            <div style="color: #e2e8f0; font-size: 24px; font-weight: 600;">3</div>
                        </div>
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Data Points / Sec</div>
                            <div style="color: #10b981; font-size: 24px; font-weight: 600;">1,247</div>
                        </div>
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Tracked Objects</div>
                            <div style="color: #3b82f6; font-size: 24px; font-weight: 600;">58,247</div>
                        </div>
                        <div>
                            <div style="color: #64748b; font-size: 13px; margin-bottom: 4px;">Ground Stations</div>
                            <div style="color: #a855f7; font-size: 24px; font-weight: 600;">147</div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    async showMissionSupport() {
        const contentArea = document.getElementById('content-area');
        
        contentArea.innerHTML = `
            <div style="height: 100%; overflow-y: auto; padding: 24px;">
                <div style="max-width: 1400px; margin: 0 auto;">
                    <!-- Header -->
                    <div style="margin-bottom: 32px;">
                        <h2 style="color: #e2e8f0; font-size: 28px; font-weight: 600; margin-bottom: 8px;">Mission Support Center</h2>
                        <p style="color: #94a3b8; font-size: 16px;">Get help, access documentation, and manage support tickets</p>
                    </div>
                    
                    <!-- Quick Actions -->
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; margin-bottom: 32px;">
                        <div style="background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%); border-radius: 12px; padding: 24px; cursor: pointer; transition: all 0.3s;"
                             onclick="missionInterface.showNewTicketForm()"
                             onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 8px 24px rgba(0, 212, 255, 0.3)';"
                             onmouseout="this.style.transform=''; this.style.boxShadow='';">
                            <div style="display: flex; align-items: center; gap: 16px;">
                                <img src="assets/support.svg" style="width: 32px; height: 32px; filter: brightness(0) invert(1);" />
                                <div>
                                    <h3 style="color: white; font-size: 18px; font-weight: 600; margin-bottom: 4px;">Create Support Ticket</h3>
                                    <p style="color: rgba(255, 255, 255, 0.9); font-size: 14px;">Get help from our mission support team</p>
                                </div>
                            </div>
                        </div>
                        
                        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 12px; padding: 24px; cursor: pointer; transition: all 0.3s;"
                             onclick="window.open('https://docs.missionready.space', '_blank')"
                             onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(0, 212, 255, 0.3)';"
                             onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                            <div style="display: flex; align-items: center; gap: 16px;">
                                <img src="assets/learn.svg" style="width: 32px; height: 32px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                                <div>
                                    <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin-bottom: 4px;">Documentation</h3>
                                    <p style="color: #94a3b8; font-size: 14px;">API references, guides, and tutorials</p>
                                </div>
                            </div>
                        </div>
                        
                        <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 12px; padding: 24px; cursor: pointer; transition: all 0.3s;"
                             onclick="missionInterface.showFAQs()"
                             onmouseover="this.style.background='rgba(15, 23, 42, 0.8)'; this.style.borderColor='rgba(16, 185, 129, 0.3)';"
                             onmouseout="this.style.background='rgba(15, 23, 42, 0.6)'; this.style.borderColor='rgba(148, 163, 184, 0.2)';">
                            <div style="display: flex; align-items: center; gap: 16px;">
                                <img src="assets/3d.svg" style="width: 32px; height: 32px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                                <div>
                                    <h3 style="color: #e2e8f0; font-size: 18px; font-weight: 600; margin-bottom: 4px;">FAQs</h3>
                                    <p style="color: #94a3b8; font-size: 14px;">Frequently asked questions</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Active Tickets -->
                    <div style="background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(148, 163, 184, 0.2); border-radius: 16px; padding: 24px;">
                        <h3 style="color: #e2e8f0; font-size: 20px; font-weight: 600; margin-bottom: 20px; display: flex; align-items: center; gap: 12px;">
                            <img src="assets/support.svg" style="width: 20px; height: 20px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                            Your Support Tickets
                        </h3>
                        
                        <div id="support-tickets-list">
                            <!-- Tickets will be loaded here -->
                            <div style="text-align: center; padding: 60px 20px; color: #64748b;">
                                <img src="assets/support.svg" style="width: 48px; height: 48px; opacity: 0.3; margin-bottom: 16px; filter: brightness(0) saturate(100%) invert(58%) sepia(84%) saturate(2044%) hue-rotate(178deg) brightness(102%) contrast(101%);" />
                                <p style="font-size: 16px;">No support tickets yet</p>
                                <p style="font-size: 14px; margin-top: 8px;">Create a ticket to get help from our team</p>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Contact Information -->
                    <div style="margin-top: 32px; padding: 20px; background: rgba(30, 41, 59, 0.3); border-radius: 12px; text-align: center;">
                        <p style="color: #94a3b8; font-size: 14px; margin-bottom: 8px;">For urgent issues, contact us directly:</p>
                        <a href="mailto:mission_support@cyberrts.com" style="color: #00d4ff; text-decoration: none; font-size: 16px; font-weight: 500;">mission_support@cyberrts.com</a>
                    </div>
                </div>
            </div>
        `;
        
        // Load tickets
        this.loadSupportTickets();
    }

    showSettings() {
        const contentArea = document.getElementById('content-area');
        contentArea.innerHTML = `
            <div class="coming-soon-container">
                <h2>System Settings</h2>
                <p>Configure your preferences and manage your account.</p>
                <p class="coming-soon">Feature coming soon...</p>
            </div>
        `;
    }
    
    destroy() {
        // Clean up intervals
        if (this.statsInterval) {
            clearInterval(this.statsInterval);
            this.statsInterval = null;
        }
        
        if (this.welcomeMessageTimer) {
            clearInterval(this.welcomeMessageTimer);
            this.welcomeMessageTimer = null;
        }
        
        // Clean up any active components
        if (this.missionControl && this.missionControl.destroy) {
            this.missionControl.destroy();
        }
        
        if (this.sdaInterface && this.sdaInterface.destroy) {
            this.sdaInterface.destroy();
        }
        
        if (this.redSky && this.redSky.destroy) {
            this.redSky.destroy();
        }
    }
}

// Export the MissionReadyInterface class
export const missionInterface = new MissionReadyInterface();
window.missionInterface = missionInterface;
